import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AdminUser, AdminRole, Permission } from '../types';
import { apiClient } from '../services/api';

interface AuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permissionSlug: string) => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Map legacy UI permission slugs → API RBAC slugs */
const PERMISSION_ALIASES: Record<string, string[]> = {
  view_orders: ['orders.read'],
  edit_orders: ['orders.write'],
  manage_products: ['products.read', 'products.write'],
  manage_inventory: ['inventory.read', 'inventory.write'],
  manage_customers: ['customers.read', 'customers.write'],
  manage_banners: ['banners.read', 'banners.write'],
  manage_headers: ['headers.read', 'headers.write'],
  manage_carousels: ['carousels.read', 'carousels.write'],
  manage_landings: ['landings.read', 'landings.write'],
  manage_short_links: ['short_links.read', 'short_links.write'],
  manage_leads: ['leads.read', 'leads.write'],
  manage_jobs: ['jobs.read', 'jobs.write'],
  manage_shipping: ['shipping.read', 'shipping.write'],
  manage_blog: ['blog.read', 'blog.write', 'reviews.read', 'reviews.write'],
  manage_reviews: ['reviews.read', 'reviews.write'],
  manage_discounts: ['coupons.read', 'coupons.write'],
  view_financial_reports: ['dashboard.read', 'audit.read'],
  manage_admins: ['admins.read', 'admins.write', 'roles.read', 'roles.write'],
  manage_ops: ['ops.read', 'ops.write'],
  manage_mattermost: ['mattermost.read'],
};

function mapAdmin(raw: Record<string, unknown>): AdminUser {
  const perms = Array.isArray(raw.permissions)
    ? (raw.permissions as Array<Record<string, string>>).map((p, i) => ({
        id: p.id ?? String(i),
        name: p.name ?? p.slug ?? '',
        slug: p.slug ?? '',
      }))
    : [];
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    email: String(raw.email ?? ''),
    phone: String(raw.phone ?? ''),
    role: (raw.role as AdminRole) || 'admin',
    permissions: perms as Permission[],
    avatar: raw.avatar ? String(raw.avatar) : undefined,
    isActive: Boolean(raw.isActive ?? true),
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const restoreSession = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setIsLoading(false);
      return;
    }
    try {
      const res = await apiClient.get<Record<string, unknown>>('/admin/auth/me');
      if (res.success && res.data) {
        setUser(mapAdmin(res.data as Record<string, unknown>));
      } else {
        localStorage.removeItem('adminToken');
        setUser(null);
      }
    } catch {
      localStorage.removeItem('adminToken');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void restoreSession();
  }, [restoreSession]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const res = await apiClient.post<{ token: string; admin: Record<string, unknown> }>(
        '/admin/auth/login',
        { email, password }
      );
      if (!res.success || !res.data?.token) {
        throw new Error(res.message || res.error || 'ورود ناموفق بود');
      }
      localStorage.setItem('adminToken', res.data.token);
      setUser(mapAdmin(res.data.admin));
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/admin/auth/logout');
    } catch {
      // ignore network errors on logout
    }
    localStorage.removeItem('adminToken');
    setUser(null);
  };

  const hasPermission = (permissionSlug: string): boolean => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const candidates = new Set<string>([permissionSlug, ...(PERMISSION_ALIASES[permissionSlug] ?? [])]);
    return user.permissions.some((p) => candidates.has(p.slug));
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    return user.role === role;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        hasPermission,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
